import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "./MediaCard.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClapperboardPlay,
  faPlay,
  faFilePdf,
} from "@fortawesome/pro-duotone-svg-icons";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import ProgressBar from "react-bootstrap/ProgressBar";
import toast from "react-hot-toast";
import { useTranslate } from "@tolgee/react";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import axiosClient from "../../../../utils/axios";
import { externalContentRootState } from "../../../../pages/externalContent";
import {
  deleteAfile,
  fileUploadStatus,
  setMediaDataAttribute,
  updateAssetUploadingState,
  updateCancelToken,
} from "../../../../redux/slices/externalContent/externalContent";
import { PLATFORM_STATIC_ICONS } from "../../../../config/icons";
import { SortableItem } from "@thaddeusjiang/react-sortable-list";

interface Props {
  item: any;
  setUploadCount: React.Dispatch<React.SetStateAction<number>>;
  setTotalUploadCount: React.Dispatch<React.SetStateAction<number>>;
}
interface MediaObject {
  uploaded: boolean;
  id: string;

  // Other properties...
}

const MediaCard: React.FC<Props> = ({
  item,
  setUploadCount,

  setTotalUploadCount,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [imageError, setImageError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const { t } = useTranslate();
  const dispatch = useDispatch();
  const postData = useSelector(
    (state: externalContentRootState) => state.externalContent.postData
  );

  const [deleting, setDeleting] = useState(false);
  const deleteAsset = async (id) => {
    const url = item?.api_url;
    // Make the POST request using Axios
    dispatch(
      deleteAfile({
        fileID: id,
      })
    );
    const recaptchaToken = await executeRecaptcha("uploadAssets");
    setDeleting(true);
    axiosClient
      .delete(url)
      .then((response) => {
        dispatch(
          deleteAfile({
            fileID: id,
          })
        );
        setDeleting(false);
        // Handle successful response
        console.log("Response:", response.data);
      })
      .catch((error) => {
        dispatch(
          deleteAfile({
            fileID: id,
          })
        );
        // Handle error
        setDeleting(false);
        console.error("Error:", error);
      });
  };

  const putWithFile = async (url, file, headers, file_type) => {
    setUploading(true);
    const source = axios.CancelToken.source();

    await dispatch(
      updateCancelToken({
        token: source,
      })
    );
    try {
      const config = {
        headers: headers ? { ...headers, "Content-Type": file_type } : {}, // Set headers property with provided headers
      };
      const response = await axios.put(url, file, {
        ...config,
        cancelToken: source?.token,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setProgress(percentCompleted);
        },
      });

      // Return the response data

      setUploadCount((prevCount) => prevCount + 1);
      setUploading(false);
      await dispatch(
        setMediaDataAttribute({
          uuid: item?.uuid,
          attribute: "isUploading",
          value: false,
        })
      );
      await dispatch(
        fileUploadStatus({
          uuid: item.uuid,
          fileID: item.id,
        })
      );

      console.log("return data", response);

      const recaptchaToken = await executeRecaptcha("uploadAssets");
      try {
        const res = await axiosClient.put(item.uploadPreset?.confirm_url, {
          success: true,
          recaptcha: recaptchaToken,
        });
        return res.data;
      } catch (err: any) {
        console.log(err);
      }

      return response.data;
    } catch (error) {
      await dispatch(
        setMediaDataAttribute({
          uuid: item?.uuid,
          attribute: "isUploading",
          value: false,
        })
      );

      if (axios.isCancel(error)) {
        console.log("Request canceled:", error.message);
      } else {
        toast.error(`${item.id}: ${t("errors.request_couldnt_be_completed")}`, {
          duration: 4000,
          position: "top-right",
        });
        const res = error.response;

        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        console.error(`Looks like there was a problem. Status Code: ${error}`);

        console.error("Error:", error);
      }

      // Dispatch deleteAfile action inside the catch block
      deleteAsset(item.id);

      setUploading(false);
      throw error;
    }
  };

  // Example usage
  const url = item.uploadPreset?.url;
  const file_type = item.file_type;
  const file = item.file;
  const headers = item.uploadPreset?.headers; // Example header

  useEffect(() => {
    const newArray = (postData.media as MediaObject[]).filter(
      (obj) => obj.uploaded === false
    );
    const firstThree = newArray.slice(0, 3)?.map((obj) => obj.id);
    const lastItem = newArray.slice(-1)[0]?.id;

    if (firstThree.includes(item.id) && !uploading) {
      putWithFile(url, file, headers, file_type)
        .then((responseData) => {
          if (lastItem === item.id) {
            setTotalUploadCount(0);
            setUploadCount(0);
            dispatch(
              updateAssetUploadingState({
                state: false,
              })
            );
          }
          console.log("Upload successful:", responseData);
        })
        .catch((error) => {
          if (lastItem === item.id) {
            setTotalUploadCount(0);
            setUploadCount(0);
            dispatch(
              updateAssetUploadingState({
                state: false,
              })
            );
          }
          console.error("Upload failed:", error);
        });
    }
  }, [postData.media]);

  return (
    <div
      style={{ position: "relative" }}
      className={`${styles["image-container"]} d-flex justify-content-center align-items-center`}
    >
      {item.media_type === "video" && (
        <div
          style={{
            position: "absolute",
            borderRadius: "2px",
            top: "10px",
            right: "13px",
          }}
        >
          <i
            className="fas fa-play"
            style={{ color: "#ffffff", fontSize: "1.25em" }}
          ></i>
        </div>
      )}

      {postData.media?.find((obj) => obj.uuid === item.uuid)?.uploaded && (
        <div
          className="w-20px position-absolute bottom-0 end-0 d-flex justify-content-center align-item-center badge  cursor-pointer"
          style={{
            backgroundColor: "#adb5bd",
            alignItems: "center",
            zIndex: 10,
          }}
          onClick={() => {
            deleteAsset(item?.id);
          }}
        >
          <div
            style={{
              aspectRatio: 1,
              alignItems: "center",
              zIndex: 10,
              cursor: "pointer",
            }}
            className="d-flex justify-content-center align-item-center rounded"
          >
            {deleting ? (
              <span
                style={{
                  aspectRatio: 1,
                  alignItems: "center",
                }}
                className="spinner-border spinner-border-sm text-white ratio ratio-1x1"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <span
                style={{
                  aspectRatio: 1,
                  alignItems: "center",
                }}
                className="text-white fw-bolder fs-7"
              >
                X
              </span>
            )}
          </div>
        </div>
      )}
      {/* {item.media_type !== "doc" && (
        <div
          style={{
            width: item.orientation === "landscape" ? "25%" : "15%",
            height: item.orientation === "portrait" ? "25%" : "15%",
            position: "absolute",
            backgroundColor: "white",
            borderRadius: "2px",
            bottom: "10px",
            right: "13px",
          }}
        ></div>
      )} */}
      {uploading && (
        <div
          style={{
            width: "75%",
            position: "absolute",
            borderRadius: "2px",
            bottom: "10px",
            margin: "0 auto 0 auto",
            zIndex: 9,
          }}
        >
          <ProgressBar striped variant="primary" animated now={progress} />
        </div>
      )}

      {!imageError && item?.preview_image && item.media_type !== "doc" ? (
        <img
          style={{ objectFit: "cover" }}
          src={item.preview_image ? item.preview_image : item.thumbnail_image}
          alt={"placeholder"}
          draggable={false}
          className={"img-thumbnail m-1 h-100px w-100px"}
          onError={() => setImageError(true)} // Set imageError to true if image fails to load
        />
      ) : item.media_type === "video" ? (
        <div
          className={`${styles["empty-state"]} h-100px w-100px bg-dark bg-opacity-25 d-flex justify-content-center align-items-center border border-white border-3 rounded-2 m-1`}
        >
          <img
            className=""
            style={{ width: "50%", height: "50%" }}
            draggable={false}
            src={PLATFORM_STATIC_ICONS.video_placeholder}
          />
        </div>
      ) : item.media_type === "doc" ? (
        <div
          className={
            "m-1 h-100px w-100px d-flex flex-column border justify-content-center align-items-center p-5 bg-white"
          }
        >
          <i
            style={{ fontSize: "70px", height: "70px" }}
            className="fa-duotone fa-file-pdf text-danger "
          ></i>
        </div>
      ) : (
        <div
          className={
            "m-1 h-100px w-100px d-flex flex-column border justify-content-center align-items-center p-5 bg-white"
          }
        >
          <i
            style={{ fontSize: "70px", height: "70px" }}
            className="fa-duotone fa-solid fa-file text-primary"
          ></i>
        </div>
      )}
    </div>
  );
};

export default MediaCard;
